import type { SupabaseClient } from '@supabase/supabase-js';
import type { ChannelReadStore } from '../ports.js';
import type { KnowledgeCandidate, StaffDigest, StaffMessage } from '../domain.js';
import { rowToStaffMessage } from '../rows.js';

/**
 * `ChannelReadStore` over PostgREST with the BROWSER's authed client, under
 * RLS — the read side of the staff channel.
 *
 * The service-role write side (`PostgrestChannelStore`, `PostgrestAnalysisStore`)
 * lives in the same subpath and touches the same tables. That is the reason
 * this is a separate class rather than extra methods there: the bridge may
 * append to `staff_messages`, and nothing shipped to a browser ever should.
 */

export const MESSAGE_COLUMNS =
  'id,wa_message_id,employee_id,sender_raw,body,has_media,sent_at,inserted_at,source';
export const DIGEST_COLUMNS = 'digest_date,body_fr,message_count';
export const CANDIDATE_COLUMNS = 'id,digest_date,body_fr';

/** A day's feed, not an archive — the views paged by day long before this port. */
const DEFAULT_MESSAGE_LIMIT = 500;
const DEFAULT_SEARCH_LIMIT = 100;
const DEFAULT_DIGEST_LIMIT = 14;

function unwrap<T>(r: { data: T[] | null; error: { message: string } | null }, what: string): T[] {
  if (r.error) throw new Error(`${what}: ${r.error.message}`);
  return r.data ?? [];
}

export class SupabaseChannelReadStore implements ChannelReadStore {
  constructor(private readonly db: SupabaseClient) {}

  async messagesBetween(fromIso: string, toIso: string, limit = DEFAULT_MESSAGE_LIMIT): Promise<StaffMessage[]> {
    const r = await this.db
      .from('staff_messages')
      .select(MESSAGE_COLUMNS)
      .gte('sent_at', fromIso)
      .lt('sent_at', toIso)
      .order('sent_at', { ascending: true })
      .limit(limit);
    return unwrap(r, 'staff_messages').map((row) => rowToStaffMessage(row as never));
  }

  async searchMessages(term: string, limit = DEFAULT_SEARCH_LIMIT): Promise<StaffMessage[]> {
    const r = await this.db
      .from('staff_messages')
      .select(MESSAGE_COLUMNS)
      .ilike('body', `%${term}%`)
      .order('sent_at', { ascending: false })
      .limit(limit);
    return unwrap(r, 'staff_messages.search').map((row) => rowToStaffMessage(row as never));
  }

  async recentDigests(limit = DEFAULT_DIGEST_LIMIT): Promise<StaffDigest[]> {
    const r = await this.db
      .from('staff_digests')
      .select(DIGEST_COLUMNS)
      .order('digest_date', { ascending: false })
      .limit(limit);
    return unwrap(r, 'staff_digests').map((row) => {
      const d = row as { digest_date: string; body_fr: string; message_count: number };
      return { digestDate: d.digest_date, bodyFr: d.body_fr, messageCount: d.message_count };
    });
  }

  async pendingCandidates(): Promise<KnowledgeCandidate[]> {
    const r = await this.db
      .from('staff_knowledge_candidates')
      .select(CANDIDATE_COLUMNS)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    return unwrap(r, 'staff_knowledge_candidates').map((row) => {
      const c = row as { id: string; digest_date: string; body_fr: string };
      // Filtered to pending by the query above; the column is not selected.
      return { id: c.id, digestDate: c.digest_date, bodyFr: c.body_fr, status: 'pending' as const };
    });
  }
}
